import { useState, useEffect, useCallback, useRef } from 'react';

const BASE = '/api';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

function Spinner({ sm }) {
  return <div className={`${sm ? 'w-3 h-3' : 'w-5 h-5'} border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin`} />;
}

// Ürün arama dropdown'u
function ProductSelect({ value, onChange, mySkus, placeholder = 'Ürün seç...' }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = mySkus.find(s => s.offer_id === value);
  const filtered = search
    ? mySkus.filter(s => s.offer_id.toLowerCase().includes(search.toLowerCase()) || (s.name || '').toLowerCase().includes(search.toLowerCase()))
    : mySkus;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setSearch(''); }}
        className={`w-full text-left px-2.5 py-1.5 border rounded-lg text-xs font-mono flex items-center justify-between gap-1 transition-colors
          ${value ? 'border-green-300 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-400'}`}
      >
        <span className="truncate">{selected ? `${selected.offer_id}` : placeholder}</span>
        <span className="text-gray-300 flex-shrink-0">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-72 bg-white border border-gray-200 rounded-xl shadow-xl">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Ara..."
              className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {value && (
              <button
                onClick={() => { onChange(''); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 border-b border-gray-50"
              >
                ✕ Seçimi kaldır
              </button>
            )}
            {filtered.slice(0, 100).map(s => (
              <button
                key={s.offer_id}
                onClick={() => { onChange(s.offer_id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${s.offer_id === value ? 'bg-blue-50 text-blue-700' : ''}`}
              >
                <div className="text-xs font-mono font-medium text-gray-800 truncate">{s.offer_id}</div>
                {s.name && <div className="text-xs text-gray-400 truncate">{s.name}</div>}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-xs text-gray-400 p-3 text-center">Sonuç yok</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SkuMapping() {
  const [sources,   setSources]   = useState([]);
  const [active,    setActive]    = useState(null); // seçili kaynak
  const [rows,      setRows]      = useState([]);   // supply_sku_map kayıtları
  const [mySkus,    setMySkus]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [savingId,  setSavingId]  = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [scanning,  setScanning]  = useState(false);
  const fileRef = useRef(null);

  // Kaynakları yükle
  useEffect(() => {
    api('GET', '/scan/sources').then(setSources).catch(console.error);
    api('GET', '/supply-map/my-skus').then(setMySkus).catch(console.error);
  }, []);

  // Seçili kaynak değişince mapping'leri yükle
  const loadRows = useCallback(async (source) => {
    if (!source) return;
    setLoading(true);
    try {
      const data = await api('GET', `/supply-map?source=${encodeURIComponent(source)}`);
      setRows(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRows(active); }, [active, loadRows]);

  // Dropdown ile my_offer_id güncelle
  async function saveMyOfferId(row, myOfferId) {
    setSavingId(row.id);
    try {
      const updated = await api('PATCH', `/supply-map/${row.id}`, {
        my_offer_id: myOfferId || null,
        my_name: mySkus.find(s => s.offer_id === myOfferId)?.name || null,
      });
      setRows(prev => prev.map(r => r.id === row.id ? updated : r));
    } catch (e) { alert('Kayıt hatası: ' + e.message); }
    finally { setSavingId(null); }
  }

  // Sistemden yükle — scan_events'ten bu kaynaktan gelen ürünler
  async function loadFromScans() {
    setScanning(true);
    try {
      const scanned = await api('GET', `/supply-map/from-scans?source=${encodeURIComponent(active)}`);
      const existingMyIds = new Set(rows.map(r => r.my_offer_id).filter(Boolean));
      const toAdd = scanned.filter(s => !existingMyIds.has(s.my_offer_id));

      if (toAdd.length === 0) {
        setImportMsg({ ok: 0, text: 'Yeni ürün bulunamadı, tümü zaten listede.' });
        return;
      }

      for (const s of toAdd) {
        await api('POST', '/supply-map', {
          source_name: active,
          my_offer_id: s.my_offer_id,
          my_name: s.my_name,
        });
      }
      await loadRows(active);
      setImportMsg({ ok: toAdd.length, text: `${toAdd.length} ürün tarama geçmişinden eklendi.` });
    } catch (e) { setImportMsg({ ok: -1, text: 'Hata: ' + e.message }); }
    finally { setScanning(false); }
  }

  // CSV yükle — karşı tarafın ürün listesi (offer_id;name formatı)
  async function handleCSV(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setImportMsg(null);
    try {
      const text  = await file.text();
      const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim());
      if (lines.length < 2) throw new Error('CSV boş');

      const headers = parseCSV(lines[0]);
      // Beklenen: Ürün Kodu;Ürün Adı (ya da offer_id;name)
      let ok = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSV(lines[i]);
        const offerId = cols[0]?.trim();
        const name    = cols[1]?.trim() || '';
        if (!offerId) continue;

        // Zaten var mı?
        const exists = rows.find(r => r.counterpart_offer_id === offerId);
        if (!exists) {
          await api('POST', '/supply-map', {
            source_name: active,
            counterpart_offer_id: offerId,
            counterpart_name: name,
          });
          ok++;
        }
      }
      await loadRows(active);
      setImportMsg({ ok, text: `${ok} karşı taraf ürünü eklendi.` });
    } catch (e) {
      setImportMsg({ ok: -1, text: 'Hata: ' + e.message });
    } finally {
      setImporting(false);
    }
  }

  function parseCSV(line) {
    const result = []; let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if ((ch === ';' || ch === ',') && !inQ) { result.push(cur); cur = ''; }
      else cur += ch;
    }
    result.push(cur);
    return result;
  }

  async function deleteRow(id) {
    await api('DELETE', `/supply-map/${id}`);
    setRows(prev => prev.filter(r => r.id !== id));
  }

  // CSV indir
  function downloadCSV() {
    const lines = ['Karşı Taraf Kodu;Karşı Taraf Adı;Benim Ürün Kodum;Benim Ürün Adım'];
    rows.forEach(r => {
      lines.push([
        r.counterpart_offer_id || '',
        r.counterpart_name || '',
        r.my_offer_id || '',
        r.my_name || '',
      ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(';'));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `sku-map-${active}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  const complete  = rows.filter(r => r.my_offer_id && r.counterpart_offer_id).length;
  const missing   = rows.filter(r => !r.my_offer_id || !r.counterpart_offer_id).length;

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">SKU Eşleşme</h1>
        <p className="text-sm text-gray-500 mt-1">Her kaynakla benim ürünlerimi eşleştir</p>
      </div>

      {/* Kaynak seçici */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {sources.map(s => (
          <button
            key={s.id}
            onClick={() => { setActive(s.name); setImportMsg(null); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors border ${
              active === s.name
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {s.name}
          </button>
        ))}
        {sources.length === 0 && (
          <p className="text-sm text-gray-400">Önce Tedarik Takibi'nden kaynak ekle</p>
        )}
      </div>

      {!active ? (
        <div className="text-center py-20 text-gray-400">
          <div className="text-4xl mb-3">☝️</div>
          <p className="text-sm">Bir kaynak seç</p>
        </div>
      ) : (
        <>
          {/* Araç çubuğu */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {/* Özet */}
            <div className="flex gap-2 flex-1">
              <span className="text-xs bg-green-50 text-green-700 border border-green-100 px-3 py-1.5 rounded-lg font-medium">
                ✓ {complete} eşleşti
              </span>
              {missing > 0 && (
                <span className="text-xs bg-yellow-50 text-yellow-700 border border-yellow-100 px-3 py-1.5 rounded-lg font-medium">
                  ⚠ {missing} eksik
                </span>
              )}
            </div>

            <button
              onClick={loadFromScans}
              disabled={scanning}
              className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:border-blue-300 text-gray-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {scanning ? <Spinner sm /> : '🔄'} Sistemden Yükle
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:border-blue-300 text-gray-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {importing ? <Spinner sm /> : '⬆'} CSV Yükle
            </button>
            <button
              onClick={downloadCSV}
              className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg transition-colors"
            >
              ⬇ CSV İndir
            </button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCSV} />
          </div>

          {/* Import mesajı */}
          {importMsg && (
            <div className={`mb-4 text-xs px-3 py-2 rounded-lg border flex items-center justify-between ${
              importMsg.ok < 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'
            }`}>
              <span>{importMsg.text}</span>
              <button onClick={() => setImportMsg(null)} className="ml-3 opacity-50 hover:opacity-100">✕</button>
            </div>
          )}

          {/* CSV format ipucu */}
          <p className="text-xs text-gray-400 mb-3">
            CSV yükle formatı: <code className="bg-gray-100 px-1 rounded">Ürün Kodu;Ürün Adı</code> (başlık satırı dahil)
          </p>

          {/* Tablo */}
          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-gray-400 bg-white border border-gray-100 rounded-2xl">
              <div className="text-4xl mb-3">📋</div>
              <p className="text-sm font-medium">Henüz eşleşme yok</p>
              <p className="text-xs mt-1">"Sistemden Yükle" ile tarama geçmişindeki ürünleri çek<br/>ya da CSV yükle ile karşı taraf ürünlerini ekle</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 w-[40%]">
                      {active}'in Ürünü
                    </th>
                    <th className="text-center px-2 py-3 text-gray-300 w-8">↔</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-500 w-[40%]">
                      Benim Ürünüm
                    </th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const bothFilled = row.my_offer_id && row.counterpart_offer_id;
                    const noneFilled = !row.my_offer_id && !row.counterpart_offer_id;
                    return (
                      <tr key={row.id}
                        className={`border-b border-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/30'} ${
                          bothFilled ? 'hover:bg-green-50/30' : 'hover:bg-yellow-50/30'
                        }`}>
                        {/* Karşı taraf */}
                        <td className="px-4 py-2.5">
                          {row.counterpart_offer_id ? (
                            <div>
                              <div className="font-mono font-medium text-gray-800">{row.counterpart_offer_id}</div>
                              {row.counterpart_name && <div className="text-gray-400 truncate max-w-[200px]">{row.counterpart_name}</div>}
                            </div>
                          ) : (
                            <span className="text-gray-300 italic">—</span>
                          )}
                        </td>

                        {/* Ok */}
                        <td className="px-2 py-2.5 text-center">
                          <span className={`text-sm ${bothFilled ? 'text-green-400' : 'text-gray-200'}`}>↔</span>
                        </td>

                        {/* Benim ürünüm — dropdown */}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1">
                              <ProductSelect
                                value={row.my_offer_id || ''}
                                onChange={val => saveMyOfferId(row, val)}
                                mySkus={mySkus}
                                placeholder="Ürün seç..."
                              />
                              {row.my_name && row.my_offer_id && (
                                <div className="text-gray-400 text-xs truncate mt-0.5 px-1">{row.my_name}</div>
                              )}
                            </div>
                            {savingId === row.id && <Spinner sm />}
                          </div>
                        </td>

                        {/* Sil */}
                        <td className="px-2 py-2.5">
                          <button
                            onClick={() => deleteRow(row.id)}
                            className="text-gray-300 hover:text-red-400 transition-colors p-1"
                            title="Sil"
                          >✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-3">
            Dropdown'dan seçince otomatik kaydedilir · Benim ürünüm sütunu tarama geçmişinden veya CSV'den gelir
          </p>
        </>
      )}
    </div>
  );
}
